import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../services/settings.service';
import { ToastService } from '../services/toast.service';
import { I18nService } from '../services/i18n.service';

interface Asset {
  src: string;
  name: string;
}

@Component({
  selector: 'app-asset-manager',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-110 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col h-[80vh]">
        <!-- Header -->
        <div class="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-slate-100/10 rounded-xl flex items-center justify-center text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/><path d="M12 12V7"/><path d="m16 16-4-4-4 4"/></svg>
            </div>
            <div>
              <h2 class="text-xl font-bold text-gray-900">{{ i18n.t('assets.title') }}</h2>
              <p class="text-xs text-gray-500">{{ i18n.t('assets.subtitle') }}</p>
            </div>
          </div>
          <button (click)="onClose.emit()" class="btn-modal-close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="6" y1="6" y2="18"/><line x1="6" x2="18" y1="6" y2="18"/></svg>
          </button>
        </div>

        <!-- Toolbar -->
        <div class="p-4 border-b border-gray-50 flex justify-between items-center gap-4 bg-white">
          <div class="relative flex-1 group">
            <svg xmlns="http://www.w3.org/2000/svg" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-slate-600 transition-colors" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <input 
              type="text" 
              [(ngModel)]="searchQuery" 
              [placeholder]="i18n.t('assets.search')" 
              class="w-full pl-10 pr-4 py-2 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-slate-500 outline-none text-sm transition-all"
            >
          </div>
          <div class="relative">
            <button class="btn-system-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
              <span>{{ i18n.t('assets.upload') }}</span>
            </button>
            <input type="file" (change)="onUpload($event)" class="absolute inset-0 opacity-0 cursor-pointer" multiple>
          </div>
        </div>

        <!-- Assets Grid -->
        <div class="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          @if (loading) {
          <div class="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
             <div class="w-8 h-8 border-4 border-slate-300 border-t-slate-800 rounded-full animate-spin"></div>
             <p class="text-sm">{{ i18n.t('assets.loading') }}</p>
          </div>
          }

          @if (!loading && filteredAssets().length === 0) {
          <div class="flex flex-col items-center justify-center h-full text-gray-400 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 mb-4 opacity-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><path d="M3 15h18"/><path d="M9 3v18"/></svg>
            <p class="text-lg font-medium text-gray-600">{{ i18n.t('assets.empty') }}</p>
            <p class="text-sm">{{ i18n.t('assets.emptyDetail') }}</p>
          </div>
          }

          @if (!loading && filteredAssets().length > 0) {
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            @for (asset of filteredAssets(); track asset.src) {
            <div 
              class="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-slate-200 transition-all relative aspect-square"
            >
              <!-- Preview -->
              <div class="h-full w-full flex items-center justify-center bg-gray-50 p-2">
                @switch (getFileType(asset.name)) {
                  @case ('image') {
                  <img [src]="asset.src" class="max-h-full max-w-full object-contain rounded-lg" [alt]="asset.name">
                  }
                  @case ('video') {
                  <div class="flex flex-col items-center gap-2 text-slate-800">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="15" x="2" y="3" rx="2" ry="2"/><circle cx="12" cy="10.5" r="2.5"/><path d="M10.5 13H5"/><path d="M19 13h-4.5"/></svg>
                    <span class="text-[10px] font-bold uppercase">Video</span>
                  </div>
                  }
                  @case ('audio') {
                  <div class="flex flex-col items-center gap-2 text-slate-800">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                    <span class="text-[10px] font-bold uppercase">Audio</span>
                  </div>
                  }
                  @default {
                  <div class="flex flex-col items-center gap-2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span class="text-[10px] font-bold uppercase">Doc</span>
                  </div>
                  }
                }
              </div>

              <!-- Overlay Actions -->
              <div class="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4 text-center">
                 <p class="text-[10px] text-white font-medium mb-3 truncate w-full">{{ asset.name }}</p>
                 <div class="flex gap-2">
                    <button (click)="onSelect(asset)" class="w-8 h-8 bg-white text-slate-900 rounded-lg flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Seleziona">
                       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </button>
                    <button (click)="onDelete(asset)" class="w-8 h-8 bg-white text-red-600 rounded-lg flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Elimina">
                       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                    </button>
                 </div>
              </div>
            </div>
            }
          </div>
          }
        </div>

        <!-- Selection Footer -->
        <div class="p-4 border-t border-gray-100 bg-white flex justify-between items-center px-6">
           <p class="text-xs text-gray-500">{{ assets.length }} {{ i18n.t('assets.total') }}</p>
           <div class="flex gap-3">
              <button (click)="onClose.emit()" class="btn-system-secondary text-xs! py-1!">{{ i18n.t('assets.close') }}</button>
           </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
  `]
})
export class AssetManagerComponent implements OnInit {
  @Output() onClose = new EventEmitter<void>();
  @Output() onAssetSelected = new EventEmitter<Asset>();

  assets: Asset[] = [];
  loading = false;
  searchQuery = '';

  constructor(
    private settingsService: SettingsService,
    private toastService: ToastService,
    public i18n: I18nService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    this.loading = true;
    this.settingsService.listAssets().subscribe({
      next: (assets) => {
        this.assets = assets;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastService.error(this.i18n.t('toast.assetError'));
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onUpload(event: any) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    this.loading = true;
    let uploadedCount = 0;
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      this.settingsService.uploadAsset(files[i]).subscribe({
        next: () => {
          uploadedCount++;
          if (uploadedCount === totalFiles) {
            this.toastService.success(`${totalFiles} ` + this.i18n.t('toast.assetUploaded'));
            this.refresh();
          }
        },
        error: () => {
          this.toastService.error(this.i18n.t('toast.assetError') + `: ${files[i].name}`);
          if (uploadedCount === totalFiles - 1) this.refresh();
        }
      });
    }
  }

  onDelete(asset: Asset) {
    if (confirm(this.i18n.t('common.deleteConfirm') + ` "${asset.name}"?`)) {
      this.settingsService.deleteAsset(asset.src).subscribe({
        next: () => {
          this.toastService.success(this.i18n.t('toast.assetDeleted'));
          this.refresh();
        },
        error: () => {
          this.toastService.error(this.i18n.t('toast.assetError'));
        }
      });
    }
  }

  onSelect(asset: Asset) {
    this.onAssetSelected.emit(asset);
    this.onClose.emit();
  }

  filteredAssets() {
    if (!this.searchQuery) return this.assets;
    const query = this.searchQuery.toLowerCase();
    return this.assets.filter(a => a.name.toLowerCase().includes(query));
  }

  getFileType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext!)) return 'image';
    if (['mp4', 'webm', 'ogg', 'mov'].includes(ext!)) return 'video';
    if (['mp3', 'wav', 'aac'].includes(ext!)) return 'audio';
    return 'other';
  }
}
